import { useEffect, useState } from 'react';
import { fetchEventosLlamada, suscribirEventosLlamada } from '../services/llamadas';
import type { LlamadaEventoPanel } from '../types/llamadas';

// Bitácora de la llamada seleccionada, para la línea de tiempo del detalle.
//
// Anclado en `call_id` y no en el uuid: un evento puede existir con id_llamada
// null si llegó antes que su llamada (la RPC lo tolera). Se recarga cuando
// cambia la llamada o su `actualizado_en` — todo hito del ciclo de vida va
// acompañado de un UPDATE de la fila, así que ese refetch ya los cubre— y
// además escucha Realtime, que es lo que hace aparecer una transferencia
// mientras la llamada sigue en curso.
//
// Un fallo devuelve [] en vez de propagarse: [] es indistinguible de "llamada
// sin bitácora", que es justo el fallback correcto (la línea de tiempo se
// reconstruye). Nunca debe tumbar el detalle por no poder pintar el log.
export function useLlamadaEventos(
  callId: string | null,
  actualizadoEn: string | null
): { eventos: LlamadaEventoPanel[]; cargando: boolean } {
  const [eventos, setEventos] = useState<LlamadaEventoPanel[]>([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!callId) {
      setEventos([]);
      setCargando(false);
      return;
    }
    let vigente = true;
    setCargando(true);
    fetchEventosLlamada(callId)
      .then((filas) => {
        if (!vigente) return;
        setEventos(filas);
        setCargando(false);
      })
      .catch(() => {
        if (!vigente) return;
        setEventos([]);
        setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [callId, actualizadoEn]);

  useEffect(() => {
    if (!callId) return;
    return suscribirEventosLlamada(callId, (nuevo) => {
      setEventos((previos) => _mergeEvento(previos, nuevo));
    });
  }, [callId]);

  return { eventos, cargando };
}

// Upsert por `id` manteniendo el orden canónico (ts y, a igualdad, creado_en).
// El dedup importa: el mismo evento puede llegar por el fetch y por Realtime, y
// la RPC hace upsert, así que un reintento de Retell reemite el INSERT.
export function _mergeEvento(
  lista: LlamadaEventoPanel[],
  e: LlamadaEventoPanel
): LlamadaEventoPanel[] {
  const sinEl = lista.filter((x) => x.id !== e.id);
  return [...sinEl, e].sort((a, b) => {
    const porTs = Date.parse(a.ts) - Date.parse(b.ts);
    return porTs !== 0 ? porTs : Date.parse(a.creado_en) - Date.parse(b.creado_en);
  });
}
